/* eslint-disable unicorn/no-null */
import { memo, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
// mantine
import {
    MultiSelect,
    ScrollArea,
    Select,
    TextInput,
    UnstyledButton
} from '@mantine/core';
// components
import { DateRangeSelect, DateRangeKey } from '@newo/ui-kit';
// utils
import { useConversationsConnectorsQuery } from '@/services/bff/bff.queries.ts';
import {
    stringToBoolean,
    booleanToString
} from '@/pages/Sessions/Sessions.utils';
import useSessionsStore from '@/pages/Sessions/Sessions.store';
// assets
import styles from '@/pages/Sessions/components/Filters/Filters.module.css';
import EditableDateTimePicker from '@/components/EditableDateTimePicker';
import ConnectorSelect from '@/modules/Integrations/components/ConnectorSelect';

interface FiltersProps {
    total: number;
}

const Filters = ({ total }: FiltersProps) => {
    const { filters, setFilters, resetFilters, dateRange, setDateRange } =
        useSessionsStore(
            useShallow((state) => ({
                filters: state.filters,
                setFilters: state.setFilters,
                resetFilters: state.resetFilters,
                dateRange: state.dateRange,
                setDateRange: state.setDateRange
            }))
        );

    const { data: integrations = [] } = useConversationsConnectorsQuery();

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setFilters({ query: value });
    };

    const handleLeadChange = (value: string | null) => {
        setFilters({ is_lead: stringToBoolean(value ?? undefined) });
    };

    const handleTestChange = (value: string | null) => {
        setFilters({ is_test: stringToBoolean(value ?? undefined) });
    };

    const handleTodoChange = (value: string | null) => {
        setFilters({ is_todo: stringToBoolean(value ?? undefined) });
    };

    const handleArchivedChange = (value: string | null) => {
        setFilters({
            is_archived: stringToBoolean(value ?? undefined)
        });
    };

    const handleOpportunityChange = (value: string | null) => {
        setFilters({
            is_opportunity: stringToBoolean(value ?? undefined)
        });
    };

    const handleAgentSuccessChange = (value: string | null) => {
        setFilters({
            agent_success: value ? Number(value) : undefined
        });
    };

    const handleAgentHumanSuccessChange = (value: string | null) => {
        setFilters({
            agent_success_human: value ? Number(value) : undefined
        });
    };

    const handleFromDateTimeChange = (fromDatetime?: string) => {
        if (fromDatetime) {
            setDateRange(undefined);
        }
        setFilters({ from_datetime: fromDatetime ?? undefined });
    };

    const handleToDateTimeChange = (toDatetime?: string) => {
        if (toDatetime) {
            setDateRange(undefined);
        }
        setFilters({ to_datetime: toDatetime ?? undefined });
    };

    const handleDateRangeChange = (interval: DateRangeKey | null) => {
        setDateRange(interval ?? undefined);
        handleFromDateTimeChange();
        handleToDateTimeChange();
    };

    const handleConnectorsChange = (connectors: string[]) => {
        setFilters({ connectors });
    };

    const handleChannelsChange = (value: string[]) => {
        setFilters({
            channels: value
        });
    };

    const handleSessionLengthsChange = (value: string[]) => {
        setFilters({
            session_lengths: value
        });
    };

    const handleClear = () => {
        resetFilters();
    };

    const dateRangeOptions = useMemo(
        () =>
            Object.values(DateRangeKey).map((key) => ({
                value: key,
                label: key
                    .replace(/^last_/, 'Last ')
                    .replaceAll('_', ' ')
                    .replaceAll(/\b\w/g, (m) => m.toUpperCase())
            })),
        []
    );

    return (
        <aside className={styles.filters}>
            <div className="flex justify-between text-xs">
                <span>Found {total} sessions</span>
                <UnstyledButton
                    className="text-xs text-blue-6"
                    onClick={handleClear}
                >
                    Reset
                </UnstyledButton>
            </div>
            <ScrollArea
                className={styles.scrollWrapper}
                mah="100%"
                offsetScrollbars="y"
                type="never"
            >
                <div className="flex flex-col gap-2 pb-20">
                    <TextInput
                        label="Search"
                        placeholder="Search by phone number, name"
                        size="xs"
                        value={filters.query ?? ''}
                        onChange={handleQueryChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Lead' },
                            { value: 'false', label: 'Non-lead' }
                        ]}
                        label="Is Lead"
                        placeholder="Select"
                        value={booleanToString(filters.is_lead)}
                        onChange={handleLeadChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Opportunity' },
                            { value: 'false', label: 'Not Opportunity' }
                        ]}
                        label="Is Opportunity"
                        placeholder="Select"
                        size="xs"
                        value={booleanToString(filters.is_opportunity) ?? null}
                        onChange={handleOpportunityChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Test' },
                            { value: 'false', label: 'Live' }
                        ]}
                        label="Is Test"
                        placeholder="Select"
                        value={booleanToString(filters.is_test)}
                        onChange={handleTestChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Yes' },
                            { value: 'false', label: 'No' }
                        ]}
                        label="Is Todo"
                        placeholder="Select"
                        value={booleanToString(filters.is_todo)}
                        onChange={handleTodoChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: 'true', label: 'Yes' },
                            { value: 'false', label: 'No' }
                        ]}
                        label="Is Archived"
                        placeholder="Select"
                        value={booleanToString(filters.is_archived)}
                        onChange={handleArchivedChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: '1', label: 'Success-A' },
                            { value: '2', label: 'Success-B' },
                            { value: '0', label: 'No' }
                        ]}
                        label="Human Assessment"
                        placeholder="Select"
                        size="xs"
                        value={
                            filters.agent_success_human
                                ? `${filters.agent_success_human}`
                                : null
                        }
                        onChange={handleAgentHumanSuccessChange}
                    />
                    <Select
                        clearable
                        data={[
                            { value: '1', label: 'Success-A' },
                            { value: '2', label: 'Success-B' },
                            { value: '0', label: 'No' }
                        ]}
                        label="Auto Assessment"
                        placeholder="Select"
                        size="xs"
                        value={
                            filters.agent_success === undefined
                                ? ''
                                : `${filters.agent_success}`
                        }
                        onChange={handleAgentSuccessChange}
                    />
                    <MultiSelect
                        clearable
                        data={[
                            { value: 'text', label: 'Text' },
                            { value: 'voice', label: 'Voice' },
                            { value: 'technical', label: 'Technical' }
                        ]}
                        label="Channels"
                        placeholder="Select"
                        size="xs"
                        value={filters.channels}
                        onChange={handleChannelsChange}
                    />
                    <MultiSelect
                        clearable
                        data={[
                            { value: 'zero', label: 'Zero' },
                            { value: 'short', label: 'Short' },
                            { value: 'regular', label: 'Regular' },
                            { value: 'long', label: 'Long' }
                        ]}
                        label="Session Lengths"
                        placeholder="Select"
                        size="xs"
                        value={filters.session_lengths}
                        onChange={handleSessionLengthsChange}
                    />
                    <ConnectorSelect
                        integrations={integrations}
                        value={filters.connectors ?? []}
                        onChange={handleConnectorsChange}
                    />
                    <DateRangeSelect
                        options={dateRangeOptions}
                        size="xs"
                        value={dateRange ?? null}
                        onChange={handleDateRangeChange}
                    />

                    <EditableDateTimePicker
                        label="From Date & Time"
                        placeholder="From Date & Time"
                        value={filters.from_datetime}
                        onChange={handleFromDateTimeChange}
                    />
                    <EditableDateTimePicker
                        label="To Date & Time"
                        placeholder="To Date & Time"
                        value={filters.to_datetime}
                        onChange={handleToDateTimeChange}
                    />
                </div>
            </ScrollArea>
        </aside>
    );
};

export default memo(Filters);
